/**
 * ContextSheet.tsx
 *
 * The chat header's unified context switcher (#1976). A single header trigger
 * (`Lobby ▾`) opens this one adaptive bottom sheet, which switches BOTH:
 *   - which CLUB you're viewing (conditional "Clubs" section — rendered only
 *     when the user belongs to >1 club, per ADR-UX-003; single-club users never
 *     see a dead-end one-item switcher), and
 *   - which CONVERSATION (the always-present RoomList).
 *
 * Replaces the Phase 1 ConversationSheet (rooms only) and folds in the retired
 * ClubSwitcherModal. Built on the shared BottomSheet primitive; #1611 read-state
 * stays owned by ClubChatPage (rooms + activity utils are passed in).
 */

import type { Room } from '../hooks/useRooms';
import type { ClubWithRole } from '../lib/api/clubs';
import BottomSheet from './BottomSheet';
import RoomList from './RoomList';
import ClubList from './ClubList';

interface ContextSheetProps {
  isOpen: boolean;
  onClose: () => void;
  // Conversations (always present)
  rooms: Room[];
  activeRoomId: string | null;
  onSelectRoom: (roomId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  hasNewActivity: (room: Room) => boolean;
  isRecentlyActive: (room: Room) => boolean;
  // Clubs (optional — the "Clubs" section renders only when >1 club)
  clubs?: ClubWithRole[];
  activeClubSlug?: string | null;
  onSelectClub?: (clubSlug: string) => void;
}

export default function ContextSheet({
  isOpen,
  onClose,
  rooms,
  activeRoomId,
  onSelectRoom,
  isLoading = false,
  error = null,
  hasNewActivity,
  isRecentlyActive,
  clubs,
  activeClubSlug,
  onSelectClub,
}: ContextSheetProps) {
  // Conditional club switching (ADR-UX-003): only when the user has >1 club AND
  // the caller wired the handler. Otherwise the sheet is conversations-only.
  const showClubs = !!(clubs && clubs.length > 1 && onSelectClub);

  // Selecting a room navigates and dismisses the sheet.
  const handleSelectRoom = (roomId: string) => {
    onSelectRoom(roomId);
    onClose();
  };

  // Switching club dismisses the sheet (caller navigates to the new club's chat).
  const handleSelectClub = (clubSlug: string) => {
    onSelectClub?.(clubSlug);
    onClose();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={showClubs ? 'Switch' : 'Conversations'}>
      {showClubs && (
        <div className="border-b border-oro-border" data-tour="context-clubs">
          <SectionHeader label="Clubs" />
          <ClubList
            clubs={clubs}
            activeClubSlug={activeClubSlug}
            onSelectClub={handleSelectClub}
          />
        </div>
      )}

      {showClubs && <SectionHeader label="Conversations" />}

      {/* Room list (presentational; #1611 activity owned by ClubChatPage) */}
      <RoomList
        rooms={rooms}
        activeRoomId={activeRoomId}
        onSelectRoom={handleSelectRoom}
        isLoading={isLoading}
        error={error}
        hasNewActivity={hasNewActivity}
        isRecentlyActive={isRecentlyActive}
      />
    </BottomSheet>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-4 py-2 bg-oro-surface-alt">
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">{label}</h3>
    </div>
  );
}
